import * as React from 'react'
import { ChevronDown, Check } from 'lucide-react'

interface SelectContextType {
  value?: string
  onValueChange?: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  selectedLabel?: string
  setSelectedLabel?: (label: string) => void
}

const SelectContext = React.createContext<SelectContextType | undefined>(
  undefined
)

const useSelectContext = () => {
  const context = React.useContext(SelectContext)
  if (!context) {
    throw new Error('Select components must be used within a Select')
  }
  return context
}

interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
}

export const Select: React.FC<SelectProps> = ({
  value,
  onValueChange,
  children,
}) => {
  const [open, setOpen] = React.useState(false)
  const [selectedLabel, setSelectedLabel] = React.useState<string>('')

  return (
    <SelectContext.Provider
      value={{
        value,
        onValueChange,
        open,
        setOpen,
        selectedLabel,
        setSelectedLabel,
      }}
    >
      <div className='relative'>{children}</div>
    </SelectContext.Provider>
  )
}

interface SelectTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
}

export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  SelectTriggerProps
>(({ className = '', children, ...props }, ref) => {
  const { open, setOpen } = useSelectContext()
  const baseClasses = 'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
  const classes = `${baseClasses} ${className}`.trim()

  return (
    <button
      ref={ref}
      type='button'
      className={classes}
      onClick={() => setOpen(!open)}
      {...props}
    >
      {children}
      <ChevronDown className='h-4 w-4 opacity-50' />
    </button>
  )
})
SelectTrigger.displayName = 'SelectTrigger'

interface SelectValueProps {
  placeholder?: string
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder }) => {
  const { selectedLabel } = useSelectContext()
  return <span>{selectedLabel || placeholder}</span>
}

interface SelectContentProps {
  children: React.ReactNode
  className?: string
}

export const SelectContent: React.FC<SelectContentProps> = ({
  children,
  className,
}) => {
  const { open, setOpen, value, setSelectedLabel } = useSelectContext()

  // Update the selected label when content mounts or value changes
  React.useEffect(() => {
    if (value && setSelectedLabel) {
      // Find the matching SelectItem and extract its label
      React.Children.forEach(children, (child) => {
        if (
          React.isValidElement<SelectItemProps>(child) &&
          child.props.value === value
        ) {
          const label = child.props.children
          if (typeof label === 'string') {
            setSelectedLabel(label)
          }
        }
      })
    }
  }, [value, children, setSelectedLabel])

  if (!open) return null

  return (
    <>
      <div className='fixed inset-0 z-40' onClick={() => setOpen(false)} />
      <div
        className={`absolute top-full left-0 z-50 w-full min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80 mt-1 ${className || ''}`.trim()}
      >
        {children}
      </div>
    </>
  )
}

interface SelectItemProps {
  value: string
  children: React.ReactNode
  className?: string
}

export const SelectItem: React.FC<SelectItemProps> = ({
  value,
  children,
  className,
}) => {
  const {
    value: selectedValue,
    onValueChange,
    setOpen,
    setSelectedLabel,
  } = useSelectContext()
  const isSelected = selectedValue === value

  const handleSelect = () => {
    onValueChange?.(value)
    // Store the label (children) when item is selected
    if (typeof children === 'string') {
      setSelectedLabel?.(children)
    }
    setOpen(false)
  }

  const baseClasses = 'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
  const classes = `${baseClasses} ${className || ''}`.trim()
  
  return (
    <div
      className={classes}
      onClick={handleSelect}
    >
      <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
        {isSelected && <Check className='h-4 w-4' />}
      </span>
      {children}
    </div>
  )
}
